import { useEffect, useMemo, useState } from "react"
import { DataStore, useDataStore, useExtras, useQuery, useRetainedEntities } from "../../Data"
import { type IAccountSubscriptionPlanInfo } from "../../Types"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"

interface AccountPlanCatalog {
  plans: IAccountSubscriptionPlanInfo[] | undefined;
  stripeKey: string | undefined;
  appAccountToken: string | undefined;
}

export function useAccountPlanCatalog(ready: boolean): AccountPlanCatalog {
  const contextStore = useDataStore() as DataStore | undefined;
  const [fallbackStore] = useState(() => new DataStore());
  const store = contextStore ?? fallbackStore;
  const plans = useQuery(store, SchemaEntities.AccountSubscriptionPlan, {});
  const features = useQuery(store, SchemaEntities.AccountSubscriptionPlanFeature, {});
  const pricing = useQuery(store, SchemaEntities.AccountSubscriptionPlanPricing, {});
  const extras = useExtras(store, SchemaEntities.AccountSubscriptionPlan, {});
  const [storeLoaded, setStoreLoaded] = useState(false);

  useRetainedEntities([
    SchemaEntities.AccountSubscriptionPlan,
    SchemaEntities.AccountSubscriptionPlanFeature,
    SchemaEntities.AccountSubscriptionPlanPricing,
  ], ready);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (store.isQueryFetched(SchemaEntities.AccountSubscriptionPlan, {})) {
      setStoreLoaded(true);
      return;
    }
    store.fetchQuery(SchemaEntities.AccountSubscriptionPlan, {}).then(() => {
      if (store.isQueryFetched(SchemaEntities.AccountSubscriptionPlan, {})) {
        setStoreLoaded(true);
      }
    });
  }, [ready, store]);

  const storedPlans = useMemo<IAccountSubscriptionPlanInfo[] | undefined>(() => {
    if (!storeLoaded) {
      return undefined;
    }
    return plans.map((plan) => ({
      plan,
      features: features.filter((feature) => feature.accountSubscriptionPlanId === plan.id),
      pricing: pricing.filter((price) => price.accountSubscriptionPlanId === plan.id),
    }));
  }, [features, plans, pricing, storeLoaded]);

  return {
    plans: storedPlans,
    stripeKey: typeof extras.stripeKey === "string" ? extras.stripeKey : undefined,
    appAccountToken: typeof extras.appAccountToken === "string" ? extras.appAccountToken : undefined,
  };
}
